import React, { useState, useEffect } from 'react';
import Slider from './Slider';
import Spinner from './Spinner';
import styles from './GalleryDisplay.module.css';

/**
 * Composant pour afficher une galerie d'images
 * @param {Object} props
 * @param {number} props.galleryId - ID de la galerie à afficher
 * @param {Object} props.gallery - Objet galerie pré-chargé (optionnel)
 * @param {string} props.apiUrl - URL de l'API pour récupérer les galeries (default: /api/gallery/)
 * @param {string} props.className - Classes CSS supplémentaires
 */
const GalleryDisplay = ({ 
  galleryId, 
  gallery = null, 
  apiUrl = '/api/gallery/', 
  className 
}) => {
  const [galleryData, setGalleryData] = useState(gallery);
  const [loading, setLoading] = useState(!gallery && !!galleryId);
  const [error, setError] = useState(null);

  // Récupérer les données de la galerie si l'ID est fourni mais pas l'objet galerie
  useEffect(() => {
    // Si nous avons déjà les données de la galerie ou pas d'ID, ne rien faire
    if (galleryData || !galleryId) return;

    setLoading(true);
    fetch(`${apiUrl}${galleryId}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Impossible de récupérer la galerie');
        }
        return response.json();
      })
      .then(data => {
        setGalleryData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Erreur lors du chargement de la galerie:', err);
        setError('Impossible de charger la galerie d\'images');
        setLoading(false);
      });
  }, [galleryId, galleryData, apiUrl]);

  // Afficher un spinner pendant le chargement
  if (loading) {
    return (
      <div className={`${styles.galleryLoading} ${className || ''}`}>
        <Spinner />
        <p>Chargement de la galerie...</p>
      </div>
    );
  }

  // Afficher un message d'erreur
  if (error) {
    return (
      <div className={`${styles.galleryError} ${className || ''}`}>
        <p>{error}</p>
      </div>
    );
  }

  // Si pas de galerie disponible
  if (!galleryData) {
    return (
      <div className={`${styles.galleryNotFound} ${className || ''}`}>
        <p>Aucune galerie disponible</p>
      </div>
    );
  }

  // Extraire les images de la galerie
  const { title, images = [] } = galleryData;

  return (
    <div className={`${styles.galleryDisplay} ${className || ''}`}>
      <Slider 
        images={images} 
        title={title} 
      />
    </div>
  );
};

export default GalleryDisplay; 